import React, { useState, useRef, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';

const AudioPlayer = () => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const audioSrc = "https://media-manager-c.questera.ai/greta-media/9156b7ad9768dabbd1486a5021f9991fb512a0d6d6c844e6b5b41380a57b274ba7bc4b27b2a4de1f0a85d0c05b5620e1/videos/dmlkZW8vbXA0/d1cac35db95361eb4e352282b7da18b8.mp4";

  // Handle play/pause toggle
  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Update progress bar as audio plays
  const handleTimeUpdate = () => {
    setProgress(audioRef.current.currentTime);
  };

  // Set duration once audio metadata is loaded
  const handleLoadedMetadata = () => {
    setDuration(audioRef.current.duration);
  };

  // Handle manual seeking
  const handleSeek = (e) => {
    const newTime = Number(e.target.value);
    audioRef.current.currentTime = newTime;
    setProgress(newTime);
  };

  // Format seconds to M:SS
  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Ensure audio pauses if component unmounts
  useEffect(() => {
    const currentAudio = audioRef.current;
    return () => {
      if (currentAudio) {
        currentAudio.pause();
      }
    };
  }, []);

  return (
    <div className="max-w-[620px] w-full mx-auto my-8 bg-[#0d152a] border border-white/10 rounded-2xl p-6 shadow-[0_12px_30px_rgba(0,0,0,0.35)] font-sans text-left relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>

      {/* Badge */}
      <div className="inline-flex items-center bg-white/5 border border-white/12 rounded-full px-4 py-1.5 text-xs font-medium text-slate-300 mb-6">
        <span className="w-2 h-2 bg-blue-400 rounded-full mr-2 shadow-[0_0_8px_rgba(96,165,250,0.8)] animate-pulse"></span>
        Live Demo • Listen to AutoReception in Action
      </div>
      
      {/* Player UI */}
      <div className="w-full bg-[#1e2230] rounded-xl border border-white/[0.08] p-5 flex items-center gap-5 relative z-10">
        
        {/* Hidden Audio Element */}
        <audio 
          ref={audioRef}
          src={audioSrc}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={() => setIsPlaying(false)}
        />

        {/* Play/Pause Button */}
        <button 
          onClick={togglePlay}
          className="w-14 h-14 shrink-0 bg-blue-600 hover:bg-blue-500 text-white rounded-full flex items-center justify-center transition-all shadow-[0_0_15px_rgba(37,99,235,0.4)] focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-[#1e2230]"
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          <SafeIcon name={isPlaying ? "Pause" : "Play"} className={`text-xl ${isPlaying ? '' : 'ml-1'}`} />
        </button>

        {/* Progress & Info */}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-end mb-2">
            <h4 className="text-white font-semibold text-sm truncate">Real Customer Call Demo</h4>
            <div className="text-xs font-medium text-slate-400 font-mono">
              {formatTime(progress)} / {formatTime(duration)}
            </div>
          </div>
          
          {/* Custom Range Slider */}
          <div className="relative w-full h-2 bg-slate-700/50 rounded-full flex items-center">
            {/* Progress Fill */}
            <div 
              className="absolute left-0 h-full bg-blue-500 rounded-full pointer-events-none"
              style={{ width: `${(progress / (duration || 1)) * 100}%` }}
            ></div>
            <input 
              type="range" 
              min="0" 
              max={duration || 100} 
              value={progress} 
              onChange={handleSeek}
              className="absolute w-full h-full opacity-0 cursor-pointer"
            />
            {/* Custom Thumb indicator */}
            <div 
              className="absolute h-3 w-3 bg-white rounded-full shadow pointer-events-none transition-transform"
              style={{ 
                left: `${(progress / (duration || 1)) * 100}%`,
                transform: 'translateX(-50%)',
              }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;